import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import MetaTags from 'components/common/MetaTags';
import AdminDataTable from 'components/admin/AdminDataTable';
import MainContent from 'components/NewDesign/MainContent';
import PageHeader from 'components/NewDesign/PageHeader';
import CustomNewButton from 'components/NewDesign/CustomNewButton/CustomNewButton';
import CustomNewConfirm from 'components/NewDesign/CustomNewConfirm';
import TableActionButtons from 'components/NewDesign/TableActionButtons';
import PinProtectedAction from 'components/common/PinProtected/PinProtectedAction';
import TeamEditPopup from 'components/admin/TeamEditPopup';
import TeamColourDot from 'components/admin/TeamColourDot';
import { deleteClubTeam, getClubTeams } from 'api/club/teamApi';
import { getErrorMessage } from 'utils/api';
import { getUserPermissionLevel } from 'services/auth/tokenService';
import { UserPermissionLevel } from 'constants/auth';
import { teamWordsOf } from 'utils/teamLabel';
import styles from './Teams.module.css';

// The club's teams: school houses, factions, colour teams. A club reads its
// own word for them throughout (utils/teamLabel.js); the page is only reached
// by a club that has teams (the menu entry and the route both test for it).
//
// Officials can read the list. Only an Admin or the Owner can add, edit or
// delete a team, and each of those asks for the admin PIN, as Age Groups do.

// words: what this club calls a team. onEdit and onDelete are left out for a
// user who may only read the list, and the Actions column with them.
const teamColumns = (words, onEdit, onDelete) => [
  {
    accessor: 'name',
    label: `${words.team} Name`,
    Cell: ({ row }) => (
      <span className={styles.teamName}>
        <TeamColourDot colour={row.colour} />
        {row.name}
      </span>
    ),
  },
  {
    accessor: 'athleteCount',
    label: 'Athletes',
  },
  {
    accessor: 'sortOrder',
    label: 'Sort Order',
  },
  ...(onEdit
    ? [
        {
          accessor: 'actions',
          label: 'Actions',
          Cell: ({ row }) =>
            TableActionButtons(row, onEdit, onDelete, {
              editTitle: `Edit ${words.lower.team}`,
              deleteTitle: `Delete ${words.lower.team}`,
            }),
        },
      ]
    : []),
];

const Teams = () => {
  const { selectedClub } = useSelector((state) => state.user || {});
  const words = teamWordsOf(selectedClub);
  const canEdit = getUserPermissionLevel() >= UserPermissionLevel.Admin;

  const [teams, setTeams] = useState([]);
  const [fetchingTeams, setFetchingTeams] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [editMode, setEditMode] = useState(null);

  // Delete confirmation state. The first delete of a team that has athletes
  // comes back 409 with a report naming how many; the second dialog shows
  // that number and sends the delete again with it.
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [impactReport, setImpactReport] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchTeams = useCallback(async () => {
    if (!selectedClub?.clubID) return;
    try {
      setFetchingTeams(true);
      const response = await getClubTeams(selectedClub.clubID, true);
      setTeams(
        (response.data || []).map((team) => ({
          ...team,
          athleteCount: team.athleteCount ?? 0,
        }))
      );
    } catch (err) {
      console.log('[Teams] Fetch teams error: ', err);
      toast.error(getErrorMessage(err));
    } finally {
      setFetchingTeams(false);
    }
  }, [selectedClub?.clubID]);

  useEffect(() => {
    fetchTeams();
  }, [fetchTeams]);

  const handleAddTeam = () => {
    setSelectedTeam(null);
    setEditMode('add');
    setShowEditPopup(true);
  };

  const handleEditTeam = (team) => {
    setSelectedTeam(team);
    setEditMode('edit');
    setShowEditPopup(true);
  };

  const handleDeleteTeam = (team) => {
    setSelectedTeam(team);
    setShowDeleteConfirm(true);
  };

  const handleEditPopupClose = async (refresh) => {
    setShowEditPopup(false);
    setEditMode(null);
    setSelectedTeam(null);
    if (refresh) {
      await fetchTeams();
    }
  };

  const closeDeleteDialogs = () => {
    setShowDeleteConfirm(false);
    setImpactReport(null);
    setSelectedTeam(null);
  };

  const performDeleteTeam = async (
    acknowledgedAthleteCount,
    acknowledgedBonusPointsMeetCount
  ) => {
    if (!selectedClub?.clubID || !selectedTeam) return;
    try {
      setIsDeleting(true);
      await deleteClubTeam(
        selectedClub.clubID,
        selectedTeam.teamID,
        acknowledgedAthleteCount,
        acknowledgedBonusPointsMeetCount
      );
      toast.success(`${words.team} deleted successfully`);
      closeDeleteDialogs();
      await fetchTeams();
    } catch (err) {
      if (err?.response?.status === 409 && err.response.data) {
        // The team has athletes or bonus points: show (or refresh) the
        // numbers for the admin to confirm. selectedTeam is kept for the
        // confirmed delete.
        setImpactReport(err.response.data);
        setShowDeleteConfirm(false);
      } else {
        console.error(err);
        toast.error(getErrorMessage(err));
        closeDeleteDialogs();
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const impactCount = impactReport?.athleteCount || 0;
  // The meets whose bonus points are deleted with the team (points slice 4c).
  const impactBonusMeets = impactReport?.bonusPointsMeetCount || 0;
  const impactDescription = [
    impactCount > 0
      ? `${impactCount} ${impactCount === 1 ? 'athlete is' : 'athletes are'} in this ${words.lower.team}. If you delete it, ${impactCount === 1 ? 'that athlete is' : 'those athletes are'} left with no ${words.lower.team}.`
      : null,
    impactBonusMeets > 0
      ? `Its bonus points at ${impactBonusMeets} ${impactBonusMeets === 1 ? 'meet' : 'meets'} are deleted too.`
      : null,
  ]
    .filter(Boolean)
    .join(' ');
  // One after the last team in the list, offered when adding a team.
  const nextSortOrder =
    teams.reduce((highest, team) => Math.max(highest, team.sortOrder || 0), 0) +
    1;

  return (
    <>
      <MetaTags title={`SplitFast | ${words.teams}`} />
      <MainContent loading={fetchingTeams}>
        <PinProtectedAction clubId={selectedClub?.clubID}>
          {({ requestPinValidation }) => {
            const pinReason = (action) =>
              `Managing ${words.lower.teams} is restricted to admin users with PIN code access. Enter the admin PIN to ${action}.`;
            const addWithPin = () =>
              requestPinValidation(
                'add-team',
                handleAddTeam,
                null,
                pinReason(`add a new ${words.lower.team}`)
              );
            const editWithPin = (team) =>
              requestPinValidation(
                'edit-team',
                handleEditTeam,
                team,
                pinReason(`edit the ${words.lower.team} "${team.name}"`)
              );
            const deleteWithPin = (team) =>
              requestPinValidation(
                'delete-team',
                handleDeleteTeam,
                team,
                pinReason(`delete the ${words.lower.team} "${team.name}"`)
              );
            const tableColumns = canEdit
              ? teamColumns(words, editWithPin, deleteWithPin)
              : teamColumns(words);
            const gridTemplateColumns = canEdit
              ? '4fr 2fr 2fr 2fr' // name, athletes, sort order, actions
              : '4fr 2fr 2fr'; // name, athletes, sort order
            return (
              <>
                <PageHeader
                  title={words.teams}
                  subTitle={`Manage the ${words.lower.teams} your athletes belong to`}
                  primarySubTitle
                  regularWeightSubTitle
                >
                  {canEdit && (
                    <div className={styles.buttonContainer}>
                      <CustomNewButton
                        label={`+ Add ${words.team}`}
                        onClick={addWithPin}
                        style={{ marginLeft: 'auto' }}
                        variant="primary"
                      />
                    </div>
                  )}
                </PageHeader>
                <AdminDataTable
                  columns={tableColumns}
                  data={teams}
                  searchInputPlaceholder={`Search ${words.teams}`}
                  headStyle={{ gridTemplateColumns }}
                  rowStyle={{ gridTemplateColumns }}
                />
                <TeamEditPopup
                  clubID={selectedClub?.clubID}
                  isOpen={showEditPopup}
                  onClose={handleEditPopupClose}
                  team={selectedTeam}
                  mode={editMode || 'add'}
                  defaultSortOrder={nextSortOrder}
                />
                {/* Delete confirmation. A team that has athletes or bonus points
                    comes back 409 and continues in the dialog below. It says
                    what a delete does to points whatever the team has (Doug,
                    2026-10-26, points slice 4c, C14: the house drops out of
                    every meet's house totals with its points). */}
                <CustomNewConfirm
                  isOpen={showDeleteConfirm}
                  onClose={closeDeleteDialogs}
                  title={`Delete ${words.team}`}
                  description={`Are you sure you want to delete the ${words.lower.team} "${selectedTeam?.name || ''}"? Its points are removed from the ${words.lower.team} totals of every meet, and its bonus points are deleted.`}
                  confirmButtonText={`Delete ${words.team}`}
                  confirmButtonVariant="danger"
                  onConfirm={() => performDeleteTeam()}
                  cancelButtonText="Cancel"
                  showWarning={true}
                  warningText="This can't be undone."
                  isLoading={isDeleting}
                />
                {/* Shown when the delete returns 409: the team has athletes,
                    or bonus points at some meets (points slice 4c). */}
                <CustomNewConfirm
                  isOpen={!!impactReport}
                  onClose={closeDeleteDialogs}
                  onCancel={closeDeleteDialogs}
                  title={`Delete ${words.team} — ${impactReport?.teamName || ''}`}
                  description={impactDescription}
                  confirmButtonText={
                    isDeleting ? 'Deleting...' : `Delete ${words.team}`
                  }
                  confirmButtonVariant="danger"
                  onConfirm={() =>
                    performDeleteTeam(impactCount, impactBonusMeets)
                  }
                  cancelButtonText="Cancel"
                  showWarning={true}
                  warningText={
                    impactCount > 0
                      ? `This can't be undone. The athletes stay in the club; you can put them in another ${words.lower.team} afterwards.`
                      : "This can't be undone."
                  }
                  isLoading={isDeleting}
                />
              </>
            );
          }}
        </PinProtectedAction>
      </MainContent>
    </>
  );
};

export default Teams;
